import { ColGroup, Picker, Select } from '@104ha/base-ui';
import { ApisType, useOptions } from 'open-api-client/src/hook/use-options';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Title } from './title';
import quickApiPrivileges from '../../constants/quickSetupApiPrivileges.json'; // 根據實際路徑調整

type PrivilegeItem = {
	apiName: string;
	apiColumns?: string[];
};

type Props = {
	privilege?: PrivilegeItem[];
	readOnly: boolean;
};

interface _PickerProps extends React.ComponentProps<typeof Picker> {
	privilege: PrivilegeItem[];
	apiName: string;
}

const _Picker = forwardRef(({ onUpdate, apiName, ...props }: _PickerProps, ref) => {
	const pickerRef = useRef<Picker>(null);

	useImperativeHandle(ref, () => ({
		getValue: () => {
			const value = pickerRef.current?.getValue();
			return {
				apiName,
				apiColumns: value,
			};
		},
		validate: () => {
			return pickerRef.current?.validate();
		},
	}));

	return <Picker ref={pickerRef} onUpdate={onUpdate} {...props} />;
});

const PrivilegeGrid = forwardRef(({ privilege = [], readOnly }: Props, ref) => {
	const { apis } = useOptions();
	const [_privilege, setPrivilege] = useState(privilege);
	const [allSelected, setAllSelected] = useState(false);
	const columnList = useRef<any[]>([]);
	const quickSetupRef = useRef<Select>(null);

	useEffect(() => {
		if (apis && privilege) {
			const allApisSelected = apis.every((api) => privilege.some((item) => item.apiName === api.apiName));
			setAllSelected(allApisSelected);
		}
		setPrivilege(privilege);
	}, [privilege, apis]);

	const apiList = apis?.map((api) => {
		return {
			...api,
			check: _privilege.find((item) => item.apiName === api.apiName),
		};
	});

	useImperativeHandle(ref, () => ({
		getValue: () => {
			const list = columnList.current
				.map((x) => {
					return x?.getValue();
				})
				.filter((x) => x);
			return _privilege.map((x) => {
				const item = list.find((y) => y.apiName === x.apiName);
				if (item) {
					return item;
				}
				return x;
			});
			// return columnList.current
			// 	.map((x) => {
			// 		return x?.getValue();
			// 	})
			// 	.filter((x) => x);
		},
		validate: () => {
			let result = true;
			for (const item of columnList.current) {
				let v = item?.validate();
				if (v === false) {
					result = v;
				}
			}
			return result;
		},
		isEnable: () => {
			return true;
		},
		isEditable: () => {
			return true;
		},
		readOnly: () => {
			return readOnly;
		},
		editable: () => {
			return true;
		},
		setValue: () => {},
	}));

	const clickEnable = (api: ApisType[0]) => {
		if (readOnly) return;
		const { apiName: name, apiColumns } = api;
		const isChecked = _privilege.find((item) => item.apiName === name);
		let arr;
		if (isChecked) {
			arr = _privilege.filter((item) => item.apiName !== name);
		} else {
			arr = [
				..._privilege,
				{
					apiName: name,
					apiColumns: apiColumns.map((x) => x.code),
				},
			];
		}
		setPrivilege(arr);
	};

	const quickSetupData = Object.keys(quickApiPrivileges).map((key) => ({
		value: key,
		label: key,
	}));

	const handleSelectUpdate = (value: string) => {
		if (!apis) {
			setPrivilege([]);
			setAllSelected(false);
			return;
		}
		const selectedApis = quickApiPrivileges[value as keyof typeof quickApiPrivileges];
		if (value === 'toggleAll') {
			quickSetupRef.current?.setValue('');
			if (allSelected) {
				setPrivilege([]);
				setAllSelected(false);
			} else {
				const newPrivilege = apis.map((api) => ({
					apiName: api.apiName,
					apiColumns: api.apiColumns.map((x) => x.code),
				}));
				setPrivilege(newPrivilege);
				setAllSelected(true);
			}
		} else if (selectedApis) {
			const newPrivilege = selectedApis.map(
				({ apiName, apiColumns }: { apiName: string; apiColumns: string[] }) => {
					const api = apis.find((a) => a.apiName === apiName);
					const allColumns = api?.apiColumns.map((x) => x.code) || [];
					const selectedColumns = apiColumns.includes('ALL') ? allColumns : apiColumns;
					return {
						apiName: api?.apiName || apiName,
						apiColumns: selectedColumns,
					};
				}
			);
			setPrivilege(newPrivilege);
			setAllSelected(false);
		} else {
			setPrivilege([]);
			setAllSelected(false);
		}
	};

	// const updateColumn = (v: string[] | string, name: string, privilege: PrivilegeItem[]) => {
	// 	const arr = privilege.map((item) => {
	// 		if (item.apiName === name) {
	// 			return {
	// 				...item,
	// 				apiColumns: typeof v === 'string' ? [v] : v,
	// 			};
	// 		}
	// 		return item;
	// 	});
	// 	setPrivilege(arr);
	// };

	return (
		<div>
			<span style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
				<Title>API權限</Title>
				{!readOnly ? (
					<div
						style={{
							display: 'inline-flex',
							marginLeft: '10%',
							alignItems: 'center',
							marginBottom: '12px',
							whiteSpace: 'nowrap',
						}}
					>
						快速設定
						<span style={{ marginLeft: '10px', width: '160px' }}>
							<Select
								ref={quickSetupRef}
								data={quickSetupData}
								behavior="jira"
								validate={{ max: 1 }}
								placeholder={'請選擇'}
								onUpdate={handleSelectUpdate}
							/>
						</span>
					</div>
				) : null}
			</span>
			<div className="H2-Grid">
				<table className="-grid">
					<ColGroup cols={[300, 300, 120, '*']} />
					<thead>
						<tr>
							<th>API代號</th>
							<th>API說明</th>
							{readOnly ? (
								<th>是否可使用</th>
							) : (
								<th onClick={() => handleSelectUpdate('toggleAll')} style={{ cursor: 'pointer' }}>
									<span className={`checkbox ${allSelected ? 'checked' : ''}`}></span>是否可使用
								</th>
							)}
							<th>回傳欄位</th>
						</tr>
					</thead>
					<tbody>
						{apiList?.map((api, index) => {
							const columns = api.apiColumns.map((column) => {
								return {
									...column,
									id: column.code,
								};
							});

							return (
								<tr key={api.apiName}>
									<td>{api.apiName}</td>
									<td>{api.apiDesc}</td>
									<td>
										<span
											onClick={() => clickEnable(api)}
											className={`checkbox ${api.check ? 'checked' : ''}`}
										></span>
									</td>
									<td>
										{api.check && columns.length > 1 && (
											<_Picker
												// onUpdate={(v) => {
												// 	const privilege = columnList.current[index]?.props?.privilege;
												// 	// console.log(privilege, v);
												// 	if (!privilege) return;
												// 	updateColumn(v, api.apiName, privilege);
												// }}
												behavior="jira"
												apiName={api.apiName}
												data={columns}
												value={api.check?.apiColumns}
												ref={(el: any) => (columnList.current[index] = el)}
												validate={{
													required: true,
												}}
												readOnly={readOnly}
												privilege={_privilege}
											/>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
});

export { PrivilegeGrid };
